import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  LogOut, BookOpen, Award, Clock, CheckCircle, Calendar, 
  ChevronDown, ChevronRight, Home, Target, Trophy,
  BarChart3, FileText, Users, HelpCircle, Eye,
  Heart, Smile, Meh, Frown, AlertTriangle, MessageCircle,
  TrendingUp, GraduationCap, School, UserCheck, ClipboardList,
  Loader2, PieChart, UserPlus, Plus, Pencil, Trash2, Menu, Video, DollarSign, MessageSquare, KeyRound
} from 'lucide-react';
import {
  ACADEMIC_CLASS_LEVELS,
  ALL_CLASS_LEVELS,
  isAcademicClass,
  formatClassLabel,
} from '@/constants/classes';
import { format } from 'date-fns';
import StudentInsights from '@/components/StudentInsights';
import UsageCapsCard from '@/components/UsageCapsCard';
import HolidayCalendar from '@/components/HolidayCalendar';
import StudentContactEditor from '@/components/StudentContactEditor';
import Timetable from '@/components/Timetable';
import FeesPanel from '@/components/FeesPanel';
import FeesPinGate from '@/components/FeesPinGate';
import PrincipalTiles from '@/components/PrincipalTiles';
import RevisionHeatmap from '@/components/RevisionHeatmap';
import ClassReadinessRollup from '@/components/ClassReadinessRollup';
import { LEAVE_TYPE_LABELS, LEAVE_TYPE_COLORS } from '@/lib/leaveTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sidebar Menu Item Component
const MenuItem = ({ icon: Icon, label, active, onClick, badge }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
        active 
          ? 'bg-purple-700 text-white' 
          : 'text-purple-100 hover:bg-purple-700/50'
      }`}
    >
      <div className="flex items-center gap-3">
        <Icon size={18} />
        <span className="font-medium text-sm uppercase tracking-wide">{label}</span>
      </div>
      {badge && (
        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{badge}</span>
      )}
    </button>
  );
};

const HeadTeacherMockExamsView = () => {
  const [mocks, setMocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMockId, setSelectedMockId] = useState(null);
  const [attempts, setAttempts] = useState(null);
  const [selectedAttemptId, setSelectedAttemptId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/head-teacher/mock-exams`);
      setMocks(res.data.mock_exams || []);
    } catch (err) {
      toast.error('Failed to load mock exams');
    } finally { setLoading(false); }
  };

  const viewAttempts = async (mockId) => {
    try {
      setSelectedMockId(mockId);
      const res = await axios.get(`${API}/head-teacher/mock-exams/${mockId}/attempts`);
      setAttempts(res.data);
    } catch (err) { toast.error('Failed'); }
  };

  if (loading) return <div className="bg-white rounded-xl p-8 text-center"><Loader2 className="animate-spin mx-auto text-purple-500" /></div>;

  // Drill into individual student attempt
  if (selectedAttemptId) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => setSelectedAttemptId(null)} data-testid="ht-back-from-attempt">
          ← Back to attempts
        </Button>
        <StudentInsights
          diagnosticUrl={`${API}/head-teacher/mock-exam-attempts/${selectedAttemptId}/diagnostic`}
          actionPlanUrl={`${API}/head-teacher/mock-exam-attempts/${selectedAttemptId}/ai-action-plan`}
        />
      </div>
    );
  }

  if (selectedMockId && attempts) {
    const m = attempts.mock_exam;
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => { setSelectedMockId(null); setAttempts(null); }}>← Back</Button>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-bold">{m?.title}</h3>
          <p className="text-xs text-gray-500">
            By {m?.created_by_name} · {attempts.attempts.length} submission(s)
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {attempts.attempts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No submissions yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-purple-900 text-white">
                <tr>
                  <th className="px-4 py-2 text-left">Student</th>
                  <th className="px-4 py-2 text-center">Score</th>
                  <th className="px-4 py-2 text-center">Percentage</th>
                  <th className="px-4 py-2 text-left">Submitted</th>
                  <th className="px-4 py-2 text-center">Insights</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {attempts.attempts.map(a => {
                  const pct = a.percentage || (m?.total_marks ? Math.round((a.auto_score / m.total_marks) * 100) : 0);
                  return (
                    <tr key={a.id}>
                      <td className="px-4 py-2">{a.student_name}</td>
                      <td className="px-4 py-2 text-center font-semibold">{a.auto_score} / {m?.total_marks}</td>
                      <td className="px-4 py-2 text-center">{pct}%</td>
                      <td className="px-4 py-2 text-xs">{a.submitted_at ? new Date(a.submitted_at).toLocaleString() : '-'}</td>
                      <td className="px-4 py-2 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedAttemptId(a.id)}
                          data-testid={`ht-insights-${a.id}`}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <ClipboardList className="text-purple-600" size={22} />
          Class Exams (School-wide)
        </h3>
        <p className="text-xs text-gray-500 mt-1">All practice papers created by teachers in your school. <span className="font-medium text-purple-600">For student practice only — not used for report cards.</span></p>
      </div>

      {mocks.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500">
          <FileText className="mx-auto mb-2 text-gray-300" size={48} />
          <p>No practice papers created yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-purple-900 text-white">
              <tr>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-center">Board</th>
                <th className="px-4 py-2 text-center">Status</th>
                <th className="px-4 py-2 text-center">Marks</th>
                <th className="px-4 py-2 text-center">Submissions</th>
                <th className="px-4 py-2 text-left">Teacher</th>
                <th className="px-4 py-2 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {mocks.map(m => (
                <tr key={m.id}>
                  <td className="px-4 py-2">
                    <p className="font-medium">{m.title}</p>
                    <p className="text-xs text-gray-500">{new Date(m.created_at).toLocaleDateString()}</p>
                  </td>
                  <td className="px-4 py-2 text-center"><Badge variant="secondary">{m.board}</Badge></td>
                  <td className="px-4 py-2 text-center">
                    <Badge variant={m.status === 'published' ? 'default' : 'outline'}>
                      {m.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-center">{m.total_marks}</td>
                  <td className="px-4 py-2 text-center font-semibold text-purple-700">{m.submissions || 0}</td>
                  <td className="px-4 py-2 text-xs">{m.created_by_name}</td>
                  <td className="px-4 py-2 text-center">
                    <Button size="sm" variant="outline" onClick={() => viewAttempts(m.id)} data-testid={`ht-view-attempts-${m.id}`}>
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const HeadTeacherDashboard = () => {
  const { user, logout } = useAuth();
  
  // Navigation State
  const [activeSection, setActiveSection] = useState('dashboard');
  
  // Data State
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [results, setResults] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [mentalHealth, setMentalHealth] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [teacherLeaves, setTeacherLeaves] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [htOnlineClasses, setHtOnlineClasses] = useState([]);
  const [htClassAttendance, setHtClassAttendance] = useState(null);

  useEffect(() => { setSidebarOpen(false); }, [activeSection]);
  
  // Filters
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedSection, setSelectedSection] = useState('all');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [bulkTargetSection, setBulkTargetSection] = useState('A');
  const [studentSearch, setStudentSearch] = useState('');
  const [filterAttendance, setFilterAttendance] = useState('all');   // all|good|ok|low|critical|none
  const [filterPerformance, setFilterPerformance] = useState('all'); // all|good|avg|weak|none
  const [filterContact, setFilterContact] = useState('all');         // all|has|missing
  const [teacherSearch, setTeacherSearch] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterTeacherClass, setFilterTeacherClass] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [resultStatus, setResultStatus] = useState('all');
  
  // Add Teacher Modal
  const [showAddTeacher, setShowAddTeacher] = useState(false);
  const [addingTeacher, setAddingTeacher] = useState(false);
  const [createdTeacher, setCreatedTeacher] = useState(null);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', subjects: [], class_levels: [], assignments: [] });
  const [teacherForm, setTeacherForm] = useState({
    name: '',
    username: '',
    password: 'Teacher@123',
    subjects: ['Mathematics'],
    class_levels: [...ACADEMIC_CLASS_LEVELS]
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Reset the global Class filter back to "All Classes" whenever the user
  // navigates to a different section. Per-section class filters (e.g. the
  // dedicated dropdown inside All Students or Timetable) can still override
  // it locally, but the header filter never carries stale state across tabs.
  useEffect(() => {
    setSelectedClass('all');
    setStudentSearch('');
    setFilterAttendance('all');
    setFilterPerformance('all');
    setFilterContact('all');
    setTeacherSearch('');
    setFilterSubject('all');
    setFilterTeacherClass('all');
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'students') {
      loadStudents();
    } else if (activeSection === 'teachers') {
      loadTeachers();
    } else if (activeSection === 'results') {
      loadResults();
    } else if (activeSection === 'analytics') {
      loadAnalytics();
    } else if (activeSection === 'mental-health') {
      loadMentalHealth();
    } else if (activeSection === 'leaves') {
      loadLeaves();
    }
  }, [activeSection, selectedClass, selectedSubject, resultStatus]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, mhRes] = await Promise.all([
        axios.get(`${API}/head-teacher/dashboard/stats`),
        axios.get(`${API}/head-teacher/mental-health/overview`).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setMentalHealth(mhRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const classParam = selectedClass !== 'all' ? `?class_level=${selectedClass}` : '';
      const [studentsRes, attRes] = await Promise.all([
        axios.get(`${API}/head-teacher/students${classParam}`),
        axios.get(`${API}/teacher/attendance/summary${selectedClass !== 'all' ? `?class_level=${selectedClass}` : ''}`).catch(() => ({ data: [] })),
      ]);
      const attMap = (attRes.data || []).reduce((acc, s) => { acc[s.student_id] = s; return acc; }, {});
      const merged = (studentsRes.data || []).map(st => ({
        ...st,
        attendance_percentage: attMap[st.id]?.percentage ?? null,
        attendance_days: attMap[st.id] ? { present: attMap[st.id].present, total: attMap[st.id].total_days } : null,
      }));
      setStudents(merged);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const loadTeachers = async () => {
    try {
      const response = await axios.get(`${API}/head-teacher/teachers`);
      setTeachers(response.data);
    } catch (error) {
      console.error('Error loading teachers:', error);
    }
  };

  const handleAddTeacher = async () => {
    if (addingTeacher) return;
    if (!teacherForm.name.trim() || !teacherForm.username.trim()) {
      toast.error('Please fill in Name and Username');
      return;
    }
    setAddingTeacher(true);
    try {
      const res = await axios.post(`${API}/head-teacher/teachers/add`, teacherForm);
      setCreatedTeacher({
        name: res.data.teacher.name,
        username: res.data.teacher.username,
        password: res.data.teacher.password
      });
      setShowAddTeacher(false);
      setTeacherForm({ name: '', username: '', password: 'Teacher@123', subjects: ['Mathematics'], class_levels: [...ACADEMIC_CLASS_LEVELS] });
      loadTeachers();
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add teacher');
    } finally {
      setAddingTeacher(false);
    }
  };

  const handleEditTeacher = async () => {
    try {
      await axios.put(`${API}/head-teacher/teachers/${editingTeacher.id}`, editForm);
      toast.success('Teacher updated successfully');
      setEditingTeacher(null);
      loadTeachers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update teacher');
    }
  };

  const handleDeleteTeacher = async (teacher) => {
    if (!window.confirm(`Are you sure you want to delete ${teacher.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/head-teacher/teachers/${teacher.id}`);
      toast.success(`${teacher.name} has been removed`);
      loadTeachers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete teacher');
    }
  };

  const openEditTeacher = (teacher) => {
    setEditForm({
      name: teacher.name,
      subjects: teacher.subjects || [],
      class_levels: teacher.class_levels || [...ACADEMIC_CLASS_LEVELS],
      assignments: (teacher.assignments || []).map(a => ({
        class_level: String(a.class_level || '6'),
        section: a.section || 'A',
        subjects: a.subjects || [],
      })),
    });
    setEditingTeacher(teacher);
  };

  const loadResults = async () => {
    try {
      let params = [];
      if (selectedClass !== 'all') params.push(`class_level=${selectedClass}`);
      if (selectedSubject !== 'all') params.push(`subject=${selectedSubject}`);
      if (resultStatus !== 'all') params.push(`status=${resultStatus}`);
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      
      const response = await axios.get(`${API}/head-teacher/results${queryString}`);
      setResults(response.data);
    } catch (error) {
      console.error('Error loading results:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/head-teacher/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadMentalHealth = async () => {
    try {
      const response = await axios.get(`${API}/head-teacher/mental-health/overview`);
      setMentalHealth(response.data);
    } catch (error) {
      console.error('Error loading mental health:', error);
    }
  };

  const loadLeaves = async () => {
    try {
      let params = [];
      if (selectedClass !== 'all') params.push(`class_level=${selectedClass}`);
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      
      const [studentRes, teacherRes] = await Promise.all([
        axios.get(`${API}/head-teacher/leaves${queryString}`),
        axios.get(`${API}/head-teacher/teacher-leaves`).catch(() => ({ data: [] }))
      ]);
      setLeaves(studentRes.data);
      setTeacherLeaves(teacherRes.data || []);
    } catch (error) {
      console.error('Error loading leaves:', error);
    }
  };

  const approveTeacherLeave = async (leaveId) => {
    try {
      await axios.patch(`${API}/head-teacher/teacher-leaves/${leaveId}/approve`);
      toast.success('Teacher leave approved');
      loadLeaves();
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const rejectTeacherLeave = async (leaveId) => {
    try {
      await axios.patch(`${API}/head-teacher/teacher-leaves/${leaveId}/reject`);
      toast.success('Teacher leave rejected');
      loadLeaves();
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'bg-green-100 text-green-700';
    if (score >= 50) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  const getMoodIcon = (mood) => {
    const icons = {
      happy: <Smile className="text-green-500" size={20} />,
      okay: <Meh className="text-blue-500" size={20} />,
      sad: <Frown className="text-yellow-500" size={20} />,
      stressed: <AlertTriangle className="text-orange-500" size={20} />,
      anxious: <AlertTriangle className="text-red-500" size={20} />
    };
    return icons[mood] || <Meh className="text-gray-400" size={20} />;
  };

  return (
    <div className="flex min-h-screen bg-gray-100" data-testid="head-teacher-dashboard">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-purple-800 to-purple-900 text-white flex flex-col shadow-xl transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        {/* Logo/Brand */}
        <div className="p-4 border-b border-purple-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <School className="text-purple-600" size={24} />
            </div>
            <div>
              <h1 className="font-bold text-lg">MyClassAI</h1>
              <p className="text-purple-300 text-xs">Head Teacher Portal</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2 overflow-y-auto">
          <MenuItem
            icon={Home}
            label="Dashboard"
            active={activeSection === 'dashboard'}
            onClick={() => setActiveSection('dashboard')}
          />

          <MenuItem
            icon={Users}
            label="All Students"
            active={activeSection === 'students'}
            onClick={() => setActiveSection('students')}
          />

          <MenuItem
            icon={GraduationCap}
            label="Teachers"
            active={activeSection === 'teachers'}
            onClick={() => setActiveSection('teachers')}
          />

          <MenuItem
            icon={BarChart3}
            label="Analytics"
            active={activeSection === 'analytics'}
            onClick={() => setActiveSection('analytics')}
          />

          <MenuItem
            icon={Calendar}
            label="Leaves"
            active={activeSection === 'leaves'}
            onClick={() => setActiveSection('leaves')}
            badge={stats?.leaves?.pending > 0 ? stats.leaves.pending : null}
          />

          <MenuItem
            icon={ClipboardList}
            label="Class Exams"
            active={activeSection === 'mock-exams'}
            onClick={() => setActiveSection('mock-exams')}
          />

          <MenuItem
            icon={Calendar}
            label="Timetable"
            active={activeSection === 'timetable'}
            onClick={() => setActiveSection('timetable')}
          />

          <MenuItem
            icon={DollarSign}
            label="Fees"
            active={activeSection === 'fees'}
            onClick={() => setActiveSection('fees')}
          />

          <MenuItem
            icon={HelpCircle}
            label="Support"
            active={activeSection === 'support'}
            onClick={() => setActiveSection('support')}
          />
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-purple-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">{user?.name?.charAt(0) || 'H'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name || 'Head Teacher'}</p>
              <p className="text-purple-300 text-xs">Head Teacher</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-purple-200 hover:text-white hover:bg-purple-700"
            onClick={logout}
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top Bar */}
        <header className="bg-white shadow-sm px-4 md:px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100"
                data-testid="ht-mobile-menu-btn"
              >
                <Menu size={24} />
              </button>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                {activeSection === 'dashboard' && 'School Overview'}
                {activeSection === 'students' && 'All Students'}
                {activeSection === 'teachers' && 'Teaching Staff'}
                {activeSection === 'results' && 'Test Results'}
                {activeSection === 'analytics' && 'School Analytics'}
                {activeSection === 'mental-health' && 'Student Well-being'}
                {activeSection === 'leaves' && 'Leave Applications'}
                {activeSection === 'online-classes' && 'Online Classes'}
                {activeSection === 'mock-exams' && 'Class Exams — School Overview'}
                {activeSection === 'timetable' && 'Class Timetable'}
                {activeSection === 'fees' && 'Fee Management'}
                {activeSection === 'sms' && 'SMS Reminders to Parents'}
                {activeSection === 'usage-caps' && 'Usage & Costs'}
                {activeSection === 'support' && 'Support'}
              </h2>
              <p className="text-gray-500 text-sm">Welcome, {user?.name || 'Head Teacher'}!</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Class Filter - Global */}
              <select 
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Classes</option>
                <option value="6">Class 6</option>
                <option value="7">Class 7</option>
                <option value="8">Class 8</option>
                <option value="9">Class 9</option>
              </select>
              <div className="text-right">
                <p className="text-xs text-gray-400">{format(new Date(), 'EEEE, dd MMM yyyy')}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-3 md:p-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-purple-500" />
            </div>
          ) : (
            <>
              {/* Dashboard View */}
              {activeSection === 'dashboard' && stats && (
                <div className="space-y-6">
                  {/* Principal Tiles — school-wide daily pulse */}
                  <PrincipalTiles />
                  {/* Holiday Calendar widget */}
                  <HolidayCalendar role="head_teacher" />
                  {/* Overview Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-5">
                      <Users size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{stats.students?.total || 0}</p>
                      <p className="text-sm opacity-80">Total Students</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-5">
                      <GraduationCap size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{stats.teachers?.total || 0}</p>
                      <p className="text-sm opacity-80">Teachers</p>
                    </div>
                  </div>

                  {/* Class-wise Breakdown — academic performance is only meaningful for 6-10 */}
                  <div className="grid grid-cols-3 gap-4">
                    {ALL_CLASS_LEVELS.map(cls => (
                      <div key={cls} className="bg-white rounded-xl shadow-sm p-6" data-testid={`class-overview-card-${cls}`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-800">{formatClassLabel(cls)}</h3>
                          <Badge className={isAcademicClass(cls) ? "bg-purple-100 text-purple-700" : "bg-amber-100 text-amber-700"}>
                            {stats.students?.by_class?.[`class_${cls}`] || 0} students
                          </Badge>
                        </div>
                        {isAcademicClass(cls) ? (
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500 text-sm">Average Score</span>
                              <span className={`font-bold ${
                                (stats.performance?.avg_scores_by_class?.[`class_${cls}`] || 0) >= 75 ? 'text-green-600' :
                                (stats.performance?.avg_scores_by_class?.[`class_${cls}`] || 0) >= 50 ? 'text-yellow-600' :
                                'text-red-600'
                              }`}>
                                {stats.performance?.avg_scores_by_class?.[`class_${cls}`] || 0}%
                              </span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${
                                  (stats.performance?.avg_scores_by_class?.[`class_${cls}`] || 0) >= 75 ? 'bg-green-500' :
                                  (stats.performance?.avg_scores_by_class?.[`class_${cls}`] || 0) >= 50 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${stats.performance?.avg_scores_by_class?.[`class_${cls}`] || 0}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2 border border-amber-200">
                            Admin-only class — attendance &amp; fees managed here. Academic features available for Classes 6-10.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-4 gap-4">
                    <button 
                      onClick={() => setActiveSection('students')}
                      className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow text-left"
                    >
                      <Users className="text-blue-500 mb-2" size={32} />
                      <p className="font-semibold text-gray-800">View Students</p>
                      <p className="text-sm text-gray-500">All classes</p>
                    </button>
                    <button 
                      onClick={() => setActiveSection('results')}
                      className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow text-left"
                    >
                      <FileText className="text-green-500 mb-2" size={32} />
                      <p className="font-semibold text-gray-800">View Results</p>
                      <p className="text-sm text-gray-500">{stats.tests?.completed_attempts || 0} attempts</p>
                    </button>
                    <button 
                      onClick={() => setActiveSection('analytics')}
                      className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow text-left"
                    >
                      <BarChart3 className="text-purple-500 mb-2" size={32} />
                      <p className="font-semibold text-gray-800">Analytics</p>
                      <p className="text-sm text-gray-500">School reports</p>
                    </button>
                  </div>

                  {/* Revision Heatmap — chapter-level class readiness */}
                  <RevisionHeatmap />

                  {/* Class Exam Readiness — per-cohort × subject rollup */}
                  <ClassReadinessRollup />
                </div>
              )}

              {/* Students View */}
              {activeSection === 'students' && (() => {
                // Build a single filter predicate that every count + the table use.
                const term = studentSearch.trim().toLowerCase();
                const attBucket = (p) => {
                  if (p == null) return 'none';
                  if (p >= 90) return 'good';
                  if (p >= 75) return 'ok';
                  if (p >= 50) return 'low';
                  return 'critical';
                };
                const perfBucket = (avg, taken) => {
                  if (!taken) return 'none';
                  if (avg >= 80) return 'good';
                  if (avg >= 60) return 'avg';
                  return 'weak';
                };
                const matchStudent = (s) => {
                  if (term && !((s.name || '').toLowerCase().includes(term) || String(s.roll_number || '').toLowerCase().includes(term))) return false;
                  if (selectedSection !== 'all' && (s.section || 'A') !== selectedSection) return false;
                  if (filterAttendance !== 'all' && attBucket(s.attendance_percentage) !== filterAttendance) return false;
                  if (filterPerformance !== 'all' && perfBucket(s.avg_score || 0, s.tests_taken || 0) !== filterPerformance) return false;
                  if (filterContact === 'has' && !s.parent_mobile) return false;
                  if (filterContact === 'missing' && s.parent_mobile) return false;
                  return true;
                };
                const filtered = students.filter(matchStudent);
                return (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-purple-700">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <h3 className="text-white font-semibold text-lg">
                          Student Directory ({filtered.length} of {students.length} students)
                        </h3>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={selectedClass}
                          onChange={(e) => setSelectedClass(e.target.value)}
                          className="bg-white text-purple-800 border border-white/30 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
                          data-testid="ht-students-class-filter"
                        >
                          <option value="all">All Classes</option>
                          {ALL_CLASS_LEVELS.map(cls => (
                            <option key={cls} value={cls}>{formatClassLabel(cls)}</option>
                          ))}
                        </select>
                        <select
                          value={selectedSection}
                          onChange={(e) => setSelectedSection(e.target.value)}
                          className="bg-white text-purple-800 border border-white/30 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
                          data-testid="ht-students-section-filter"
                        >
                          <option value="all">All Sections</option>
                          {['A','B','C','D','E'].map(sec => (
                            <option key={sec} value={sec}>Section {sec}</option>
                          ))}
                        </select>
                        <select
                          value={filterAttendance}
                          onChange={(e) => setFilterAttendance(e.target.value)}
                          className="bg-white text-purple-800 border border-white/30 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
                          data-testid="ht-students-attendance-filter"
                        >
                          <option value="all">Attendance</option>
                          <option value="good">Good (≥ 90%)</option>
                          <option value="ok">OK (75–89%)</option>
                          <option value="low">Low (50–74%)</option>
                          <option value="critical">Critical (&lt; 50%)</option>
                          <option value="none">Not marked yet</option>
                        </select>
                        <select
                          value={filterPerformance}
                          onChange={(e) => setFilterPerformance(e.target.value)}
                          className="bg-white text-purple-800 border border-white/30 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
                          data-testid="ht-students-performance-filter"
                        >
                          <option value="all">Performance</option>
                          <option value="good">Strong (≥ 80%)</option>
                          <option value="avg">Average (60–79%)</option>
                          <option value="weak">Needs help (&lt; 60%)</option>
                          <option value="none">No tests taken</option>
                        </select>
                        <input
                          type="text"
                          value={studentSearch}
                          onChange={(e) => setStudentSearch(e.target.value)}
                          placeholder="Search…"
                          className="bg-white text-gray-800 placeholder-gray-400 border border-white/30 rounded-lg px-3 py-1.5 text-sm focus:outline-none w-32"
                          data-testid="ht-students-search"
                        />
                        {(filterAttendance !== 'all' || filterPerformance !== 'all' || studentSearch || selectedClass !== 'all' || selectedSection !== 'all') && (
                          <button
                            onClick={() => { setFilterAttendance('all'); setFilterPerformance('all'); setFilterContact('all'); setStudentSearch(''); setSelectedClass('all'); setSelectedSection('all'); }}
                            className="text-xs underline text-white/80 hover:text-white"
                            data-testid="ht-students-clear-filters"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Per-class breakdown badges */}
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="ht-students-class-counts">
                      {['6','7','8','9'].map(cls => {
                        const count = students.filter(s => String(s.class_level) === cls).length;
                        const active = selectedClass === cls;
                        return (
                          <button
                            key={cls}
                            onClick={() => setSelectedClass(active ? 'all' : cls)}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? 'bg-white text-purple-700 border-white' : 'bg-white/10 text-white border-white/30 hover:bg-white/20'}`}
                            data-testid={`ht-students-class-chip-${cls}`}
                          >
                            Class {cls}: <span className="font-bold">{selectedClass === 'all' ? count : (active ? students.length : '—')}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Bulk-move toolbar */}
                  {selectedStudentIds.length > 0 && (
                    <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 flex flex-wrap items-center gap-3" data-testid="ht-bulk-section-toolbar">
                      <span className="text-sm font-semibold text-amber-900">
                        {selectedStudentIds.length} student{selectedStudentIds.length > 1 ? 's' : ''} selected
                      </span>
                      <span className="text-sm text-amber-800">Move to:</span>
                      <select
                        value={bulkTargetSection}
                        onChange={(e) => setBulkTargetSection(e.target.value)}
                        className="bg-white text-gray-800 border border-amber-300 rounded-md px-2 py-1 text-sm"
                        data-testid="ht-bulk-target-section"
                      >
                        {['A','B','C','D','E'].map(sec => (
                          <option key={sec} value={sec}>Section {sec}</option>
                        ))}
                      </select>
                      <button
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API}/head-teacher/students/bulk-section`, {
                              student_ids: selectedStudentIds,
                              section: bulkTargetSection,
                            });
                            toast.success(`Moved ${res.data.modified} student(s) to Section ${res.data.section}`);
                            setSelectedStudentIds([]);
                            await loadStudents();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to move students');
                          }
                        }}
                        className="px-3 py-1 bg-amber-600 text-white rounded-md text-sm font-semibold hover:bg-amber-700"
                        data-testid="ht-bulk-move-btn"
                      >
                        Move
                      </button>
                      <button
                        onClick={() => setSelectedStudentIds([])}
                        className="text-xs underline text-amber-800 hover:text-amber-900"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-purple-900 text-white text-sm">
                        <tr>
                          <th className="px-3 py-3 text-center w-10">
                            <input
                              type="checkbox"
                              checked={filtered.length > 0 && filtered.every(s => selectedStudentIds.includes(s.id))}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedStudentIds(filtered.map(s => s.id));
                                } else {
                                  setSelectedStudentIds([]);
                                }
                              }}
                              data-testid="ht-select-all-students"
                            />
                          </th>
                          <th className="px-4 py-3 text-left">Name</th>
                          <th className="px-4 py-3 text-center">Roll</th>
                          <th className="px-4 py-3 text-center">Class</th>
                          <th className="px-4 py-3 text-center">Section</th>
                          <th className="px-4 py-3 text-left min-w-[290px]">Parent contact &amp; address</th>
                          <th className="px-4 py-3 text-center">Attendance</th>
                          <th className="px-4 py-3 text-center">Tests</th>
                          <th className="px-4 py-3 text-center">Avg %</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {(() => {
                          if (filtered.length === 0) {
                            return (
                              <tr>
                                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                                  {students.length === 0
                                    ? 'No students found. Teachers will add students from their dashboard.'
                                    : 'No students match your filters.'}
                                </td>
                              </tr>
                            );
                          }
                          return filtered.map((student, index) => {
                          const p = student.attendance_percentage;
                          const days = student.attendance_days;
                          const attBadge = p == null
                            ? 'bg-gray-100 text-gray-400'
                            : p >= 90 ? 'bg-emerald-100 text-emerald-700'
                            : p >= 75 ? 'bg-amber-100 text-amber-700'
                            : p >= 50 ? 'bg-orange-100 text-orange-700'
                            : 'bg-red-100 text-red-700';
                          const isChecked = selectedStudentIds.includes(student.id);
                          return (
                          <tr key={student.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedStudentIds(prev => [...prev, student.id]);
                                  } else {
                                    setSelectedStudentIds(prev => prev.filter(id => id !== student.id));
                                  }
                                }}
                                data-testid={`ht-select-student-${student.id}`}
                              />
                            </td>
                            <td className="px-4 py-3 font-medium">{student.name}</td>
                            <td className="px-4 py-3 text-center">{student.roll_number}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge variant="outline">Class {student.class_level}</Badge>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">{student.section || 'A'}</Badge>
                            </td>
                            <td className="px-4 py-3 align-top">
                              <StudentContactEditor
                                student={student}
                                onSaved={(updated) => setStudents(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s))}
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${attBadge}`}
                                title={days ? `${days.present}/${days.total} days present` : 'No attendance recorded'}
                                data-testid={`ht-att-pct-${student.id}`}
                              >
                                {p == null ? '—' : `${p}%`}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">{student.tests_taken || 0}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(student.avg_score || 0)}`}>
                                {student.avg_score || 0}%
                              </span>
                            </td>
                          </tr>
                        );});
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
                );
              })()}

              {/* Teachers View */}
              {activeSection === 'teachers' && (() => {
                const tSearch = teacherSearch.trim().toLowerCase();
                const filteredTeachers = teachers.filter(t => {
                  if (tSearch && !((t.name || '').toLowerCase().includes(tSearch) || (t.username || '').toLowerCase().includes(tSearch))) return false;
                  if (filterSubject !== 'all' && !(t.subjects || []).includes(filterSubject)) return false;
                  if (filterTeacherClass !== 'all' && !((t.class_levels || []).includes(filterTeacherClass))) return false;
                  return true;
                });
                return (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-purple-700">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div>
                        <h3 className="text-white font-semibold text-lg">Teaching Staff ({filteredTeachers.length} of {teachers.length})</h3>
                        <p className="text-purple-200 text-sm mt-1">Filter by subject taught or class assigned</p>
                      </div>
                      <Dialog open={showAddTeacher} onOpenChange={setShowAddTeacher}>
                        <DialogTrigger asChild>
                          <Button size="sm" className="bg-white text-purple-700 hover:bg-purple-100">
                            <UserPlus className="w-4 h-4 mr-2" />
                            Add Teacher
                          </Button>
                        </DialogTrigger>
                      <DialogContent className="bg-white">
                        <DialogHeader>
                          <DialogTitle>Add New Teacher</DialogTitle>
                          <DialogDescription>Create a new teacher account for your school</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label>Full Name *</Label>
                            <Input
                              value={teacherForm.name}
                              onChange={(e) => setTeacherForm({ ...teacherForm, name: e.target.value })}
                              placeholder="e.g., Ravi Kumar"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Username *</Label>
                            <Input
                              value={teacherForm.username}
                              onChange={(e) => setTeacherForm({ ...teacherForm, username: e.target.value })}
                              placeholder="e.g., ravi_math"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Password</Label>
                            <Input
                              value={teacherForm.password}
                              onChange={(e) => setTeacherForm({ ...teacherForm, password: e.target.value })}
                              placeholder="Teacher@123"
                            />
                            <p className="text-xs text-amber-700">
                              Default: <span className="font-mono font-semibold">Teacher@123</span>. Teacher must change it on first login. You can override above.
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label>Subject *</Label>
                            <div className="flex flex-wrap gap-2">
                              {['Mathematics', 'Science', 'English', 'Social Studies'].map(subj => (
                                <button
                                  key={subj}
                                  type="button"
                                  onClick={() => {
                                    const current = teacherForm.subjects;
                                    const updated = current.includes(subj)
                                      ? current.filter(s => s !== subj)
                                      : [...current, subj];
                                    if (updated.length > 0) setTeacherForm({ ...teacherForm, subjects: updated });
                                  }}
                                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                    teacherForm.subjects.includes(subj)
                                      ? 'bg-purple-600 text-white border-purple-600'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                  data-testid={`subject-toggle-${subj.toLowerCase()}`}
                                >
                                  {subj}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Classes *</Label>
                            <div className="flex flex-wrap gap-2">
                              {ACADEMIC_CLASS_LEVELS.map(cl => (
                                <button
                                  key={cl}
                                  type="button"
                                  onClick={() => {
                                    const current = teacherForm.class_levels;
                                    const updated = current.includes(cl)
                                      ? current.filter(c => c !== cl)
                                      : [...current, cl];
                                    if (updated.length > 0) setTeacherForm({ ...teacherForm, class_levels: updated });
                                  }}
                                  className={`px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                    teacherForm.class_levels.includes(cl)
                                      ? 'bg-purple-600 text-white border-purple-600'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                  data-testid={`class-toggle-${cl}`}
                                >
                                  Class {cl}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowAddTeacher(false)} disabled={addingTeacher}>Cancel</Button>
                          <Button onClick={handleAddTeacher} className="bg-purple-600 hover:bg-purple-700" disabled={addingTeacher} data-testid="confirm-add-teacher-btn">
                            {addingTeacher ? <><Loader2 size={16} className="mr-2 animate-spin" />Adding...</> : 'Add Teacher'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    {/* Success Credentials Dialog */}
                    <Dialog open={!!createdTeacher} onOpenChange={() => setCreatedTeacher(null)}>
                      <DialogContent className="bg-white">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2 text-green-700">
                            <CheckCircle size={20} /> Teacher Created Successfully!
                          </DialogTitle>
                        </DialogHeader>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3" data-testid="teacher-credentials-popup">
                          <div className="flex justify-between">
                            <span className="text-gray-600 font-medium">Name:</span>
                            <span className="font-semibold">{createdTeacher?.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 font-medium">Username:</span>
                            <span className="font-semibold font-mono">{createdTeacher?.username}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 font-medium">Password:</span>
                            <span className="font-semibold font-mono">{createdTeacher?.password}</span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500">Share these credentials with the teacher. They can change the password after first login.</p>
                        <DialogFooter>
                          <Button
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="copy-credentials-btn"
                            onClick={() => {
                              navigator.clipboard.writeText(`Username: ${createdTeacher?.username}\nPassword: ${createdTeacher?.password}`);
                              toast.success('Credentials copied to clipboard!');
                            }}
                          >
                            Copy Credentials
                          </Button>
                          <Button variant="outline" onClick={() => setCreatedTeacher(null)}>Close</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    {/* Edit Teacher Modal */}
                    <Dialog open={!!editingTeacher} onOpenChange={(open) => !open && setEditingTeacher(null)}>
                      <DialogContent className="bg-white">
                        <DialogHeader>
                          <DialogTitle>Edit Teacher - {editingTeacher?.name}</DialogTitle>
                          <DialogDescription>Update subjects and class assignments</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label>Full Name</Label>
                            <Input
                              value={editForm.name}
                              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                              data-testid="edit-teacher-name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Subjects</Label>
                            <div className="flex flex-wrap gap-2">
                              {['Mathematics', 'Science', 'English', 'Social Studies'].map(subj => (
                                <button
                                  key={subj}
                                  type="button"
                                  onClick={() => {
                                    const updated = editForm.subjects.includes(subj)
                                      ? editForm.subjects.filter(s => s !== subj)
                                      : [...editForm.subjects, subj];
                                    if (updated.length > 0) setEditForm({ ...editForm, subjects: updated });
                                  }}
                                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                    editForm.subjects.includes(subj)
                                      ? 'bg-purple-600 text-white border-purple-600'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                  data-testid={`edit-subject-${subj.toLowerCase()}`}
                                >
                                  {subj}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Classes</Label>
                            <div className="flex flex-wrap gap-2">
                              {ACADEMIC_CLASS_LEVELS.map(cl => (
                                <button
                                  key={cl}
                                  type="button"
                                  onClick={() => {
                                    const updated = editForm.class_levels.includes(cl)
                                      ? editForm.class_levels.filter(c => c !== cl)
                                      : [...editForm.class_levels, cl];
                                    if (updated.length > 0) setEditForm({ ...editForm, class_levels: updated });
                                  }}
                                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                    editForm.class_levels.includes(cl)
                                      ? 'bg-indigo-600 text-white border-indigo-600'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                  data-testid={`edit-class-${cl}`}
                                >
                                  Class {cl}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Section Assignments (restricts which students they see) */}
                          <div className="space-y-2 border-t pt-4">
                            <div className="flex items-center justify-between">
                              <Label>Section Assignments</Label>
                              <button
                                type="button"
                                onClick={() => setEditForm({
                                  ...editForm,
                                  assignments: [...(editForm.assignments || []), { class_level: '6', section: 'A', subjects: editForm.subjects.slice(0, 1) }]
                                })}
                                className="text-xs text-indigo-600 hover:underline font-medium"
                                data-testid="add-assignment-row"
                              >
                                + Add Section
                              </button>
                            </div>
                            <p className="text-xs text-gray-500">
                              Specify which (Class, Section) this teacher handles. <strong>If empty, they see all sections</strong> of their selected classes.
                            </p>
                            {(editForm.assignments || []).length === 0 && (
                              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                                No specific sections assigned — teacher will see <em>all</em> sections.
                              </p>
                            )}
                            <div className="space-y-2">
                              {(editForm.assignments || []).map((a, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-gray-50 border rounded-md p-2" data-testid={`assignment-row-${idx}`}>
                                  <div className="col-span-3">
                                    <Label className="text-xs">Class</Label>
                                    <select
                                      value={a.class_level}
                                      onChange={(e) => {
                                        const next = [...editForm.assignments];
                                        next[idx] = { ...next[idx], class_level: e.target.value };
                                        setEditForm({ ...editForm, assignments: next });
                                      }}
                                      className="w-full border rounded-md px-2 py-1 text-sm"
                                      data-testid={`assignment-class-${idx}`}
                                    >
                                      {['6','7','8','9'].map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                  </div>
                                  <div className="col-span-3">
                                    <Label className="text-xs">Section</Label>
                                    <select
                                      value={a.section}
                                      onChange={(e) => {
                                        const next = [...editForm.assignments];
                                        next[idx] = { ...next[idx], section: e.target.value };
                                        setEditForm({ ...editForm, assignments: next });
                                      }}
                                      className="w-full border rounded-md px-2 py-1 text-sm"
                                      data-testid={`assignment-section-${idx}`}
                                    >
                                      {['A','B','C','D','E'].map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                  </div>
                                  <div className="col-span-5">
                                    <Label className="text-xs">Subjects</Label>
                                    <div className="flex flex-wrap gap-1">
                                      {(editForm.subjects || []).map(subj => {
                                        const checked = (a.subjects || []).includes(subj);
                                        return (
                                          <button
                                            key={subj}
                                            type="button"
                                            onClick={() => {
                                              const next = [...editForm.assignments];
                                              const cur = next[idx].subjects || [];
                                              next[idx] = {
                                                ...next[idx],
                                                subjects: checked ? cur.filter(s => s !== subj) : [...cur, subj],
                                              };
                                              setEditForm({ ...editForm, assignments: next });
                                            }}
                                            className={`text-xs px-2 py-0.5 rounded border ${checked ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300'}`}
                                          >
                                            {subj}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                  <div className="col-span-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const next = editForm.assignments.filter((_, i) => i !== idx);
                                        setEditForm({ ...editForm, assignments: next });
                                      }}
                                      className="text-red-500 hover:bg-red-50 rounded p-1"
                                      data-testid={`remove-assignment-${idx}`}
                                      title="Remove"
                                    >
                                      ×
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setEditingTeacher(null)}>Cancel</Button>
                          <Button onClick={handleEditTeacher} className="bg-purple-600 hover:bg-purple-700" data-testid="save-edit-teacher-btn">
                            Save Changes
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    </div>
                    {/* Filter controls */}
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <select
                        value={filterSubject}
                        onChange={(e) => setFilterSubject(e.target.value)}
                        className="bg-white text-purple-800 border border-white/30 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
                        data-testid="ht-teachers-subject-filter"
                      >
                        <option value="all">All Subjects</option>
                        <option value="Mathematics">Mathematics</option>
                        <option value="Science">Science</option>
                        <option value="English">English</option>
                        <option value="Social Studies">Social Studies</option>
                        <option value="Hindi">Hindi</option>
                        <option value="Telugu">Telugu</option>
                      </select>
                      <select
                        value={filterTeacherClass}
                        onChange={(e) => setFilterTeacherClass(e.target.value)}
                        className="bg-white text-purple-800 border border-white/30 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
                        data-testid="ht-teachers-class-filter"
                      >
                        <option value="all">All Classes</option>
                        <option value="6">Class 6</option>
                        <option value="7">Class 7</option>
                        <option value="8">Class 8</option>
                        <option value="9">Class 9</option>
                      </select>
                      <input
                        type="text"
                        value={teacherSearch}
                        onChange={(e) => setTeacherSearch(e.target.value)}
                        placeholder="Search name or username…"
                        className="bg-white text-gray-800 placeholder-gray-400 border border-white/30 rounded-lg px-3 py-1.5 text-sm focus:outline-none min-w-[220px]"
                        data-testid="ht-teachers-search"
                      />
                      {(teacherSearch || filterSubject !== 'all' || filterTeacherClass !== 'all') && (
                        <button
                          onClick={() => { setTeacherSearch(''); setFilterSubject('all'); setFilterTeacherClass('all'); }}
                          className="text-xs underline text-white/90 hover:text-white"
                          data-testid="ht-teachers-clear-filters"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-purple-900 text-white text-sm">
                        <tr>
                          <th className="px-4 py-3 text-left">Name</th>
                          <th className="px-4 py-3 text-left">Username</th>
                          <th className="px-4 py-3 text-left">Subjects</th>
                          <th className="px-4 py-3 text-left">Classes</th>
                          <th className="px-4 py-3 text-center">Tests</th>
                          <th className="px-4 py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredTeachers.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                              {teachers.length === 0
                                ? 'No teachers found. Click "Add Teacher" to create one.'
                                : 'No teachers match your filters.'}
                            </td>
                          </tr>
                        ) : filteredTeachers.map((teacher, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3 font-medium">{teacher.name}</td>
                            <td className="px-4 py-3 text-gray-500">{teacher.username}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {teacher.subjects?.map((subj, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{subj}</Badge>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {(teacher.class_levels || ['6','7','8']).map((cl, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-purple-50">Class {cl}</Badge>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center font-medium">{teacher.tests_created || 0}</td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex justify-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                  data-testid={`edit-teacher-${teacher.username}`}
                                  onClick={() => openEditTeacher(teacher)}
                                >
                                  <Pencil size={14} className="mr-1" /> Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-amber-700 border-amber-200 hover:bg-amber-50"
                                  data-testid={`reset-teacher-${teacher.username}`}
                                  onClick={async () => {
                                    if (!window.confirm(`Reset password for ${teacher.name} to Teacher@123?\n\nThey will be asked to set a new password on their next login.`)) return;
                                    try {
                                      const res = await axios.post(`${API}/head-teacher/teachers/${teacher.id}/reset-password`);
                                      setCreatedTeacher({
                                        name: teacher.name,
                                        username: res.data.username,
                                        password: res.data.password,
                                      });
                                      toast.success('Password reset. Share the new credentials with the teacher.');
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Reset failed');
                                    }
                                  }}
                                >
                                  <KeyRound size={14} className="mr-1" /> Reset PW
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  data-testid={`delete-teacher-${teacher.username}`}
                                  onClick={() => handleDeleteTeacher(teacher)}
                                >
                                  <Trash2 size={14} className="mr-1" /> Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                );
              })()}

              {/* Results View */}
              {activeSection === 'results' && (
                <div className="space-y-4">
                  {/* Filters */}
                  <div className="bg-white rounded-xl p-4 shadow-sm flex gap-4 items-center">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Subject:</label>
                      <select 
                        value={selectedSubject}
                        onChange={(e) => setSelectedSubject(e.target.value)}
                        className="border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="all">All Subjects</option>
                        <option value="Mathematics">Mathematics</option>
                        <option value="Science">Science</option>
                        <option value="English">English</option>
                        <option value="Social Studies">Social Studies</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Status:</label>
                      <select 
                        value={resultStatus}
                        onChange={(e) => setResultStatus(e.target.value)}
                        className="border rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="all">All Status</option>
                        <option value="pending">Pending Review</option>
                        <option value="approved">Approved</option>
                      </select>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-purple-700">
                      <h3 className="text-white font-semibold text-lg">All Test Results ({results.length} results)</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-purple-900 text-white text-sm">
                          <tr>
                            <th className="px-4 py-3 text-left">Student</th>
                            <th className="px-4 py-3 text-left">Test</th>
                            <th className="px-4 py-3 text-center">Class</th>
                            <th className="px-4 py-3 text-center">Subject</th>
                            <th className="px-4 py-3 text-center">Score</th>
                            <th className="px-4 py-3 text-center">Status</th>
                            <th className="px-4 py-3 text-center">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {results.map((result, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-4 py-3 font-medium">{result.student_name}</td>
                              <td className="px-4 py-3">{result.test_title}</td>
                              <td className="px-4 py-3 text-center">
                                <Badge variant="outline">Class {result.class_level}</Badge>
                              </td>
                              <td className="px-4 py-3 text-center text-sm">{result.subject}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(result.score)}`}>
                                  {result.score}%
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {result.approved ? (
                                  <Badge className="bg-green-100 text-green-700">Approved</Badge>
                                ) : (
                                  <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center text-sm text-gray-500">
                                {result.completed_at ? format(new Date(result.completed_at), 'dd MMM yyyy') : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Analytics View */}
              {activeSection === 'analytics' && analytics && (
                <div className="space-y-6">
                  {/* Exam-type Cards (Unit / Quarterly / Half-Yearly / Finals) */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-xl p-5">
                      <ClipboardList size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{stats?.tests?.by_exam_type?.unit || 0}</p>
                      <p className="text-sm opacity-80">Unit Tests</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-5">
                      <ClipboardList size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{stats?.tests?.by_exam_type?.quarterly || 0}</p>
                      <p className="text-sm opacity-80">Quarterly</p>
                    </div>
                    <div className="bg-gradient-to-br from-pink-500 to-pink-600 text-white rounded-xl p-5">
                      <ClipboardList size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{stats?.tests?.by_exam_type?.half_yearly || 0}</p>
                      <p className="text-sm opacity-80">Half-Yearly</p>
                    </div>
                    <div className="bg-gradient-to-br from-rose-500 to-rose-600 text-white rounded-xl p-5">
                      <ClipboardList size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{stats?.tests?.by_exam_type?.annual || 0}</p>
                      <p className="text-sm opacity-80">Finals</p>
                    </div>
                  </div>

                  {/* Overall Stats */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <PieChart className="text-purple-500" size={20} />
                      School-wide Performance
                    </h3>
                    <div className="text-center p-6 bg-purple-50 rounded-xl">
                      <p className="text-4xl font-bold text-purple-600">{analytics.overall?.avg_score || 0}%</p>
                      <p className="text-gray-500 mt-1">Overall Average Score</p>
                    </div>
                  </div>

                  {/* Class × Subject Matrix */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <PieChart className="text-indigo-500" size={20} />
                      Class × Subject Averages
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">Average score per class for each subject (school-wide).</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="ht-class-subject-matrix">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="text-left px-4 py-3 font-semibold text-gray-700">Class</th>
                            {['Mathematics', 'Science', 'English', 'Social Studies'].map(s => (
                              <th key={s} className="text-center px-4 py-3 font-semibold text-gray-700">{s}</th>
                            ))}
                            <th className="text-center px-4 py-3 font-semibold text-gray-700">Class Avg</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ACADEMIC_CLASS_LEVELS.map(cls => {
                            const row = analytics.by_class_subject?.[`class_${cls}`] || {};
                            const subjects = ['Mathematics', 'Science', 'English', 'Social Studies'];
                            const scores = subjects.map(s => row[s]?.avg_score || 0).filter(v => v > 0);
                            const classAvg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length * 10) / 10 : 0;
                            const colorFor = (v) => v === 0 ? 'text-gray-300' : v >= 75 ? 'text-green-600' : v >= 50 ? 'text-yellow-600' : 'text-red-600';
                            return (
                              <tr key={cls} className="border-t hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium text-gray-800">Class {cls}</td>
                                {subjects.map(s => {
                                  const v = row[s]?.avg_score || 0;
                                  return (
                                    <td key={s} className={`text-center px-4 py-3 font-semibold ${colorFor(v)}`}>
                                      {v ? `${v}%` : '—'}
                                    </td>
                                  );
                                })}
                                <td className={`text-center px-4 py-3 font-bold ${colorFor(classAvg)}`}>
                                  {classAvg ? `${classAvg}%` : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* By Class */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">Performance by Class</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {ACADEMIC_CLASS_LEVELS.map(cls => {
                        const classData = analytics.by_class?.[`class_${cls}`];
                        return (
                          <div key={cls} className="border rounded-xl p-4">
                            <h4 className="font-semibold text-lg mb-3">Class {cls}</h4>
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Students</span>
                                <span className="font-medium">{classData?.students_count || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Avg Score</span>
                                <span className={`font-bold ${
                                  (classData?.avg_score || 0) >= 75 ? 'text-green-600' :
                                  (classData?.avg_score || 0) >= 50 ? 'text-yellow-600' : 'text-red-600'
                                }`}>
                                  {classData?.avg_score || 0}%
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 text-sm">Above 75%</span>
                                <span className="text-green-600">{classData?.above_75_percent || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 text-sm">Below 50%</span>
                                <span className="text-red-600">{classData?.below_50_percent || 0}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* By Subject */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">Performance by Subject</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {['Mathematics', 'Science', 'English', 'Social Studies'].map(subject => {
                        const subjectData = analytics.by_subject?.[subject];
                        return (
                          <div key={subject} className="border rounded-xl p-4">
                            <h4 className="font-semibold mb-3">{subject}</h4>
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Avg Score</span>
                                <span className={`font-bold ${
                                  (subjectData?.avg_score || 0) >= 75 ? 'text-green-600' :
                                  (subjectData?.avg_score || 0) >= 50 ? 'text-yellow-600' : 'text-red-600'
                                }`}>
                                  {subjectData?.avg_score || 0}%
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 text-sm">Above 75%</span>
                                <span className="text-green-600">{subjectData?.above_75_percent || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 text-sm">50–75%</span>
                                <span className="text-yellow-600">{subjectData?.between_50_75 || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 text-sm">Below 50%</span>
                                <span className="text-red-600">{subjectData?.below_50_percent || 0}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Mental Health View */}
              {activeSection === 'mental-health' && mentalHealth && (
                <div className="space-y-6">
                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-pink-500 to-pink-600 text-white rounded-xl p-5">
                      <Heart size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{mentalHealth.today_checkins || 0}</p>
                      <p className="text-sm opacity-80">Today's Check-ins</p>
                    </div>
                    <div className={`${mentalHealth.talk_requests > 0 ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-gray-400 to-gray-500'} text-white rounded-xl p-5`}>
                      <MessageCircle size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{mentalHealth.talk_requests || 0}</p>
                      <p className="text-sm opacity-80">Talk Requests</p>
                    </div>
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-5">
                      <AlertTriangle size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{mentalHealth.high_stress_count || 0}</p>
                      <p className="text-sm opacity-80">High Stress</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-5">
                      <Smile size={32} className="mb-2 opacity-80" />
                      <p className="text-3xl font-bold">{mentalHealth.mood_distribution?.happy || 0}</p>
                      <p className="text-sm opacity-80">Happy Today</p>
                    </div>
                  </div>

                  {/* Check-ins by Class */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">Check-ins by Class</h3>
                    <div className="grid grid-cols-3 gap-4">
                      {ACADEMIC_CLASS_LEVELS.map(cls => (
                        <div key={cls} className="text-center p-4 bg-gray-50 rounded-xl">
                          <p className="text-2xl font-bold text-purple-600">
                            {mentalHealth.checkins_by_class?.[`class_${cls}`] || 0}
                          </p>
                          <p className="text-gray-500">Class {cls}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Talk Requests */}
                  {mentalHealth.talk_request_details?.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden border-2 border-red-200">
                      <div className="px-6 py-4 border-b bg-red-50">
                        <h3 className="font-semibold text-red-700 flex items-center gap-2">
                          <MessageCircle size={20} />
                          Students Requesting to Talk
                        </h3>
                      </div>
                      <div className="p-4">
                        {mentalHealth.talk_request_details.map((req, index) => (
                          <div key={index} className="flex items-center justify-between py-3 border-b last:border-0">
                            <div className="flex items-center gap-3">
                              {getMoodIcon(req.mood)}
                              <div>
                                <p className="font-medium">{req.student_name}</p>
                                <p className="text-sm text-gray-500">Class {req.class_level}</p>
                              </div>
                            </div>
                            {req.note && (
                              <p className="text-sm italic text-gray-500 max-w-md truncate">"{req.note}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Online Classes View */}
              {activeSection === 'online-classes' && (
                <div className="space-y-4">
                  {/* Attendance Modal */}
                  {htClassAttendance && (
                    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setHtClassAttendance(null)}>
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5">
                          <div className="flex justify-between items-start">
                            <div>
                              <h2 className="text-lg font-bold">{htClassAttendance.title}</h2>
                              <p className="text-indigo-100 text-sm mt-1">{htClassAttendance.attended_count}/{htClassAttendance.total_students} students ({htClassAttendance.attendance_percentage}%)</p>
                            </div>
                            <button onClick={() => setHtClassAttendance(null)} className="text-white/80 hover:text-white text-2xl">&times;</button>
                          </div>
                          <div className="flex gap-4 mt-3">
                            <div className="bg-green-500/30 px-3 py-1 rounded-lg text-sm">Present: {htClassAttendance.attended_count}</div>
                            <div className="bg-red-500/30 px-3 py-1 rounded-lg text-sm">Absent: {htClassAttendance.missed_count}</div>
                          </div>
                        </div>
                        <div className="overflow-y-auto" style={{maxHeight: 'calc(80vh - 140px)'}}>
                          {htClassAttendance.attended.length > 0 && (
                            <div className="p-4">
                              <h4 className="text-sm font-semibold text-green-700 mb-2">Present</h4>
                              <div className="space-y-1">
                                {htClassAttendance.attended.map((s, i) => (
                                  <div key={i} className="flex justify-between px-3 py-2 bg-green-50 rounded-lg text-sm">
                                    <span>{s.student_name}</span>
                                    <span className="text-green-600 text-xs">{s.joined_at ? new Date(s.joined_at).toLocaleTimeString('en-IN', {hour:'2-digit', minute:'2-digit'}) : ''}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {htClassAttendance.missed.length > 0 && (
                            <div className="p-4 pt-0">
                              <h4 className="text-sm font-semibold text-red-700 mb-2">Absent</h4>
                              <div className="space-y-1">
                                {htClassAttendance.missed.map((s, i) => (
                                  <div key={i} className="px-3 py-2 bg-red-50 rounded-lg text-sm">{s.student_name}</div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-4 md:px-6 py-4 border-b bg-gradient-to-r from-indigo-600 to-purple-600">
                      <h3 className="text-white font-semibold text-lg">All Scheduled Classes</h3>
                    </div>
                    {htOnlineClasses.length === 0 ? (
                      <div className="p-8 text-center text-gray-400">No online classes scheduled yet</div>
                    ) : (
                      <div className="divide-y">
                        {htOnlineClasses.map((oc, idx) => {
                          const isUpcoming = new Date(oc.scheduled_at) > new Date();
                          return (
                            <div key={idx} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-semibold text-gray-800">{oc.title}</h4>
                                  <Badge className={isUpcoming ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}>
                                    {isUpcoming ? 'Upcoming' : 'Completed'}
                                  </Badge>
                                  <Badge variant="outline">{oc.subject} - Class {oc.class_level}</Badge>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">
                                  Teacher: <span className="font-medium">{oc.teacher_name}</span>
                                  {oc.topic && ` | ${oc.topic}`}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {new Date(oc.scheduled_at).toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                  {' '}({oc.duration_minutes} min)
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-indigo-600 border-indigo-200"
                                onClick={async () => {
                                  try {
                                    const res = await axios.get(`${API}/teacher/online-classes/${oc.id}/attendance`);
                                    setHtClassAttendance(res.data);
                                  } catch (error) {
                                    toast.error('Failed to load attendance');
                                  }
                                }}
                              >
                                {oc.attendees?.length || 0} Attended — View
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Mock Exams View */}
              {activeSection === 'mock-exams' && (
                <HeadTeacherMockExamsView />
              )}

              {/* Leaves View */}
              {activeSection === 'leaves' && (
                <div className="space-y-6">
                  {/* Teacher Leaves */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gradient-to-r from-teal-600 to-teal-700">
                      <h3 className="text-white font-semibold text-lg">Teacher Leave Applications ({teacherLeaves.length})</h3>
                    </div>
                    {teacherLeaves.length === 0 ? (
                      <div className="p-6 text-center text-gray-400">No teacher leave applications</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-teal-900 text-white text-sm">
                            <tr>
                              <th className="px-4 py-3 text-left">Teacher</th>
                              <th className="px-4 py-3 text-left">Dates</th>
                              <th className="px-4 py-3 text-left">Type</th>
                              <th className="px-4 py-3 text-left">Notes</th>
                              <th className="px-4 py-3 text-center">Status</th>
                              <th className="px-4 py-3 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {teacherLeaves.map((leave, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-4 py-3 font-medium">{leave.teacher_name}</td>
                                <td className="px-4 py-3 text-sm">
                                  {leave.leave_date ? format(new Date(leave.leave_date), 'dd MMM') : '-'}
                                  {leave.end_date ? ` - ${format(new Date(leave.end_date), 'dd MMM yyyy')}` : ''}
                                </td>
                                <td className="px-4 py-3">
                                  {leave.leave_type ? (
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded border ${LEAVE_TYPE_COLORS[leave.leave_type] || 'bg-gray-100 text-gray-700 border-gray-300'}`}>
                                      {LEAVE_TYPE_LABELS[leave.leave_type] || leave.leave_type}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700">{leave.reason}</td>
                                <td className="px-4 py-3 text-center">
                                  {leave.status === 'approved' && <Badge className="bg-green-100 text-green-700">Approved</Badge>}
                                  {leave.status === 'rejected' && <Badge className="bg-red-100 text-red-700">Rejected</Badge>}
                                  {leave.status === 'pending' && <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {leave.status === 'pending' && (
                                    <div className="flex justify-center gap-2">
                                      <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white" onClick={() => approveTeacherLeave(leave.id)} data-testid={`approve-teacher-leave-${index}`}>
                                        Approve
                                      </Button>
                                      <Button size="sm" variant="outline" className="text-red-500 border-red-200" onClick={() => rejectTeacherLeave(leave.id)} data-testid={`reject-teacher-leave-${index}`}>
                                        Reject
                                      </Button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Student Leaves */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-purple-700">
                      <h3 className="text-white font-semibold text-lg">Student Leave Applications ({leaves.length})</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-purple-900 text-white text-sm">
                          <tr>
                            <th className="px-4 py-3 text-left">Student</th>
                            <th className="px-4 py-3 text-center">Class</th>
                            <th className="px-4 py-3 text-left">Dates</th>
                            <th className="px-4 py-3 text-left">Reason</th>
                            <th className="px-4 py-3 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {leaves.map((leave, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-4 py-3 font-medium">{leave.student_name}</td>
                              <td className="px-4 py-3 text-center">
                                <Badge variant="outline">Class {leave.class_level}</Badge>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {leave.leave_date ? format(new Date(leave.leave_date), 'dd MMM') : '-'} 
                                {leave.end_date ? ` - ${format(new Date(leave.end_date), 'dd MMM yyyy')}` : ''}
                              </td>
                              <td className="px-4 py-3">{leave.reason}</td>
                              <td className="px-4 py-3 text-center">
                                {leave.status === 'approved' && <Badge className="bg-green-100 text-green-700">Approved</Badge>}
                                {leave.status === 'rejected' && <Badge className="bg-red-100 text-red-700">Rejected</Badge>}
                                {leave.status === 'pending' && <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Timetable View */}
              {activeSection === 'timetable' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-sm font-medium text-gray-700">Class:</label>
                    <select
                      value={selectedClass !== 'all' ? selectedClass : '6'}
                      onChange={e => setSelectedClass(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm bg-white"
                      data-testid="timetable-class-select"
                    >
                      {ALL_CLASS_LEVELS.map(c => <option key={c} value={c}>{formatClassLabel(c)}</option>)}
                      {['LKG', 'UKG', '1', '2', '3', '4', '5', '10'].map(c => <option key={c} value={c}>{formatClassLabel(c)}</option>)}
                    </select>
                  </div>
                  <Timetable classLevel={selectedClass !== 'all' ? selectedClass : '6'} editable />
                </div>
              )}

              {/* Fees View — gated by a PIN the HT sets on first visit */}
              {activeSection === 'fees' && (
                <FeesPinGate userId={user?.id || 'self'}>
                  <FeesPanel selectedClass={selectedClass} />
                </FeesPinGate>
              )}

              {activeSection === 'usage-caps' && (
                <UsageCapsCard />
              )}

              {/* Support View */}
              {activeSection === 'support' && (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                  <HelpCircle className="mx-auto text-gray-300 mb-4" size={64} />
                  <h3 className="text-xl font-semibold text-gray-600">Need Assistance?</h3>
                  <p className="text-gray-400 mt-2">Contact technical support for help</p>
                  <p className="text-gray-500 mt-4">Email: support@myclassai.net</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default HeadTeacherDashboard;
